import * as React from 'react';
import {
  Box,
  Button,
  Collapse,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  MenuItem,
  FormControl,
  Select,
  SelectChangeEvent,
  ListItemText,
  Checkbox,
  OutlinedInput,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import BlackWhiteSwitch from '../components/common/BlackWhiteSwitch';
import CreateButtonWithModal from '../components/common/CreateButtonWithModal';
import Tooltip from '../components/common/Tooltip';

// Mock data creation
function createData(
  servicesFeesName: string,
  countryMarket: string,
  customerGroup: string,
  supplier: string,
  hotelName: string,
  fromDate: string,
  toDate: string,
  firstDetails: Record<string, string>
) {
  return {
    servicesFeesName,
    countryMarket,
    customerGroup,
    supplier,
    hotelName,
    fromDate,
    toDate,
    firstDetails
  };
}

// Sample rows
const rows = [
  createData(
    'Services Fees A',
    'India',
    'Retail',
    'Supplier X',
    'Burj Khalifa',
    '2023-01-01',
    '2023-12-31',
    {
      'Booking Window': '2023-01-01 to 2023-12-31',
    'Discount on': 'All',
    'Discount type': 'Percentage',
    'Discount value': '10',
    'Status': 'Active',
    }
  ),
];

// Subtable fields
const fareFields = ['Booking Window', 'Discount on', 'Discount type', 'Discount value', 'Status'];

// Subtable component
function HorizontalSubtable({
  title,
  fields,
  firstDetails,
}: {
  title: string;
  fields: string[];
  firstDetails: Record<string, string>;
}) {
  return (
    <>
    
    <Box mb={4} className="innerTable">
      <Table size="small">
        <TableHead>
          <TableRow>
            {fields.map((field) => (
              <TableCell key={field} sx={{ fontWeight: 'bold' }}>{field}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            {fields.map((field) => (
              <TableCell
              key={field}
              className={
                field === 'Status'
                  ? firstDetails[field] === 'Active'
                    ? 'ActiveClass'
                    : 'InActiveClass'
                  : undefined
              }
            >
              {firstDetails[field] || '-'}
            </TableCell>
            ))}
          </TableRow>
        </TableBody>
      </Table>
    </Box>
    </>
  );
}
 
// Expandable row
function Row({ row }: { row: ReturnType<typeof createData> }) {
  const [open, setOpen] = React.useState(false);

  return (
    <>
    
      <TableRow>
        <TableCell>{row.servicesFeesName}</TableCell>
        <TableCell>{row.countryMarket}</TableCell>
        <TableCell>{row.customerGroup}</TableCell>
        <TableCell>{row.supplier}</TableCell>
        <TableCell>{row.hotelName}</TableCell>
        <TableCell>{row.fromDate}</TableCell>
        <TableCell>{row.toDate}</TableCell>
        <TableCell align="center">
          <IconButton size="small" onClick={() => setOpen(!open)} className="viewBtn">
            {open ? (
              <>
                View Less <KeyboardArrowUpIcon />
              </>
            ) : (
              <>
                View More <KeyboardArrowDownIcon />
              </>
            )}
          </IconButton>
        </TableCell>
        <TableCell align="center">
                  <Tooltip />
                </TableCell>
      </TableRow>

      <TableRow>
        <TableCell colSpan={9} style={{ paddingBottom: 0, paddingTop: 0 }} className='innerTableBG'>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box margin={2}>
              <HorizontalSubtable
                title=""
                fields={fareFields}
                firstDetails={row.firstDetails}
              />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;

  const MenuProp = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 250,
      },
    },
    anchorOrigin: {
      vertical: 'bottom' as 'bottom',
      horizontal: 'left' as 'left',
    },
    transformOrigin: {
      vertical: 'top' as 'top',
      horizontal: 'left' as 'left',
    },
    getContentAnchorEl: null, // Important for older versions of MUI v4; can omit for MUI v5+
  };

const label = [
  'Services Fees Name',
  'Country / Market',
  'Customer Group',
  'Supplier',
  'Hotel Name',
  'From Date',
  'To Date',
];

// Main Component
export default function StayDiscounts() {
    const [labelName, setLabelName] = React.useState<string[]>([]);
  
    const labelChange = (event: SelectChangeEvent<typeof labelName>) => {
      const {
        target: { value },
      } = event;
      setLabelName(
        // On autofill we get a stringified value.
        typeof value === 'string' ? value.split(',') : value,
      );
    };

    
  return (
    <>
    <Box className="headingBtnWrap">
      <Typography variant="h1">Discounts</Typography>
      <CreateButtonWithModal modalTitle="Discounts">
        <Box className="modalFormWrap">
          <Box component="form" className="modalForm">
            <Box className="feeModalForm">
              <Typography variant="subtitle1" gutterBottom>Source</Typography>
              <Box className="feeInputFieldWrap">
              {['Services Fees Name', 'Country / Market', 'Customer Group', 'Supplier' ].map((label) => (
                <div className="form-control inputFieldWrap" key={label}>
                  <label>{label}</label>
                  <input type="text" className="inputField" />
                </div>
              ))}
              </Box>
              <Typography variant="subtitle1" gutterBottom>Condition criteria</Typography>
              <Box className="feeInputFieldWrap">
              {['Hotel Name' ].map((label) => (
                <div className="form-control inputFieldWrap" key={label}>
                  <label>{label}</label>
                  <input type="text" className="inputField" />
                </div>
              ))}
              </Box>
              <Typography variant="subtitle1" gutterBottom>Applicability Criteria</Typography>
              <Box className="feeInputFieldWrap">
              {['From Date', 'To Date', 'Booking Window' ].map((label) => (
                <div className="form-control inputFieldWrap" key={label}>
                  <label>{label}</label>
                  <input type="text" className="inputField" />
                </div>
              ))}
              </Box>
              <Typography variant="subtitle1" gutterBottom>Pricing Logic</Typography>
              <Box className="feeInputFieldWrap">
              {['Discount on', 'Discount type', 'Discount value' ].map((label) => (
                <div className="form-control inputFieldWrap" key={label}>
                  <label>{label}</label>
                  <input type="text" className="inputField" />
                </div>
              ))}
              </Box>
            </Box>
          </Box>
          <Box display="flex" justifyContent="flex-end">
            <Button variant="contained" className='formBtn'>Save</Button>
          </Box>
        </Box>
        <BlackWhiteSwitch />
      </CreateButtonWithModal>
    </Box>
    <Box className="searchInputWrap">
      <Box
        display="flex"
        alignItems="center"
        gap={2}
        p={2}
        sx={{ width: '100%'}}
        className="searchInputFilterBox"
      >
        <FormControl sx={{ minWidth: 460 }}>
           <Select
            className='leftFilterBox'
            labelId="demo-multiple-checkbox-label"
            id="demo-multiple-checkbox"
            multiple
            value={labelName}
            onChange={labelChange}
            displayEmpty
            input={<OutlinedInput />}
            renderValue={(selected) => {
              if (selected.length === 0) {
                return 'Select';  // <-- Placeholder text
              }
              return selected.join(', ');
            }}
            MenuProps={MenuProp}
          >
            <MenuItem disabled value="">
            Select
            </MenuItem>
            {label.map((name) => (
              <MenuItem key={name} value={name} className='selectDropdown'>
                <Checkbox checked={labelName.includes(name)} />
                <ListItemText primary={name} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {/* <TextField
          className="leftSearchFilter"
          fullWidth
          variant="outlined"
          placeholder="Search"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter') handleSearch();
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <IconButton onClick={handleSearch} edge="start">
                  <SearchIcon />
                </IconButton>
              </InputAdornment>
            ),
            endAdornment: (
              <Button
                variant="contained"
                className="searchBtn"
                color="primary"
                type="submit"
                sx={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                onClick={handleSearch}
              >
                Search
              </Button>
            ),
          }}
        /> */}
        <Button
                variant="contained"
                className="searchBtn"
                color="primary"
                type="submit"
                sx={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
              >
                Search
              </Button>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }} className="dynamicInputFieldWrap">
        {labelName.map((item) => (
          <div key={item} className="form-control inputFieldWrap">
            <label style={{ marginBottom: '4px', fontWeight: '500' }}>{item}</label>
            <input
              type="text"
              // placeholder={`Enter ${item}`}
              className="inputField"
            />
          </div>
        ))}
      </Box>
    </Box>
    <Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Services Fees Name</TableCell>
              <TableCell>Country / Market</TableCell>
              <TableCell>Customer Group</TableCell>
              <TableCell>Supplier</TableCell>
              <TableCell>Hotel Name</TableCell>
              <TableCell>From Date</TableCell>
              <TableCell>To Date</TableCell>
              <TableCell align="center">Actions</TableCell>
              <TableCell align="center">Audit Trail</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => (
              <Row key={index} row={row} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
    </>
  );
}
